import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fetchAndValidateCartItems, updateCartItemQuantityWithCustomer, removeFromCartWithCustomer } from '../services/cartService';
import CondoEatsLogo from './CondoEats Logo (horizontal).png';
import WalletIcon from './Wallet icon with logo.png';
import ConfirmModal from './ConfirmModal';
import './TopBar.css';

/**
 * Get user initials from their name
 * @param {string} name - User's full name
 * @returns {string} - Up to 2 character initials
 */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// SVG path for the gradient backdrop
const svgPath = "M4 0H1444V129H4V0Z";

export default function TopBar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);
  const [cartDropdownOpen, setCartDropdownOpen] = useState(false);
  const { user, profile, isAuthenticated, logout, cartRefreshTrigger, refreshCart } = useAuth();
  const { showError, showSuccess } = useToast();

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const cartDropdownRef = useRef(null);

  const [cartItems, setCartItems] = useState([]);
  const [loadingCart, setLoadingCart] = useState(false);
  const [cartError, setCartError] = useState(null);

  // Fetch cart items from database
  useEffect(() => {
    const fetchCartItems = async () => {
      if (!user) {
        setCartItems([]);
        return;
      }

      try {
        setLoadingCart(true);
        setCartError(null);

        const { items, removedCount } = await fetchAndValidateCartItems(user.$id);

        if (removedCount > 0) {
          showError(`${removedCount} item(s) removed as they are no longer available.`);
        }

        setCartItems(items);
      } catch (error) {
        console.error('Error fetching cart items:', error);
        setCartError('Failed to load cart items');
        showError(error, 'Failed to load cart items');
      } finally {
        setLoadingCart(false);
      }
    };

    fetchCartItems();
  }, [user, cartRefreshTrigger]);



  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileUserMenuOpen(false);
  };

  const toggleMobileUserMenu = () => {
    setMobileUserMenuOpen(!mobileUserMenuOpen);
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  const closeProfileDropdown = () => {
    setProfileDropdownOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      closeProfileDropdown();
      closeMobileMenu();
      showSuccess('Logged out successfully');

      // Small delay to show toast before navigation
      setTimeout(() => {
        navigate('/', { replace: true });
        // Force a state refresh by scrolling to top
        window.scrollTo(0, 0);
      }, 500);
    } catch (error) {
      console.error('Logout failed:', error);
      showError(error, 'Failed to log out. Please try again.');
    }
  };

  const toggleCartDropdown = () => {
    setCartDropdownOpen(!cartDropdownOpen);
  };

  const closeCartDropdown = () => {
    setCartDropdownOpen(false);
  };

  // Debouncing for quantity updates
  const debounceTimers = useRef({});

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) {
      setItemToRemove(itemId);
      setShowConfirmModal(true);
      return;
    }

    // 1. UPDATE LOCAL STATE IMMEDIATELY (Optimistic UI)
    setCartItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    ));

    // 2. DEBOUNCE THE SERVER SYNC
    if (debounceTimers.current[itemId]) {
      clearTimeout(debounceTimers.current[itemId]);
    }

    debounceTimers.current[itemId] = setTimeout(async () => {
      try {
        await updateCartItemQuantityWithCustomer(user.$id, itemId, newQuantity);
        console.log(`[Cart] Quantity synced for ${itemId}: ${newQuantity}`);
        delete debounceTimers.current[itemId];
        
        // Refresh cart in context avoided to prevent loop
        // if (refreshCart) refreshCart();
      } catch (error) {
        console.error('Error syncing quantity:', error);
        showError(error, 'Failed to sync quantity with server.');
        
        // Optionally revert local state if sync fails
        // (For simplicity, we just show the error, but a real app might re-fetch)
      }
    }, 500); // 500ms delay
  };

  const handleDeleteClick = (itemId) => {
    setItemToRemove(itemId);
    setShowConfirmModal(true);
  };

  const confirmRemove = async () => {
    if (itemToRemove) {
      await removeItem(itemToRemove);
      setShowConfirmModal(false);
      setItemToRemove(null);
    }
  };

  const cancelRemove = () => {
    setShowConfirmModal(false);
    setItemToRemove(null);
  };

  const removeItem = async (itemId) => {
    try {
      await removeFromCartWithCustomer(user.$id, itemId);

      // Update local state
      setCartItems(prev => prev.filter(item => item.id !== itemId));
      showSuccess('Item removed from cart');

      // Refresh cart in context to keep other components (CheckoutPage) in sync
      if (refreshCart) refreshCart();
    } catch (error) {
      console.error('Error removing item:', error);
      showError(error, 'Failed to remove item. Please try again.');
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleReviewOrder = () => {
    closeCartDropdown();
    navigate('/checkout');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close dropdowns if clicking on the confirmation modal
      if (showConfirmModal) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (cartDropdownRef.current && !cartDropdownRef.current.contains(event.target)) {
        setCartDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showConfirmModal]);

  return (
    <div className="topbar-container" data-name="Top Bar">
      <div className="topbar-backdrop" data-name="Nav Bar backdrop">
        <div className="topbar-backdrop-inner">
          <svg className="topbar-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 1448 137">
            <g filter="url(#filter0_d_116_3)" id="Nav Bar backdrop">
              <path d={svgPath} fill="url(#paint0_linear_116_3)" />
            </g>
            <defs>
              <filter colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse" height="137" id="filter0_d_116_3" width="1448" x="0" y="0">
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
                <feOffset dy="4" />
                <feGaussianBlur stdDeviation="2" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                <feBlend in2="BackgroundImageFix" mode="normal" result="effect1_dropShadow_116_3" />
                <feBlend in="SourceGraphic" in2="effect1_dropShadow_116_3" mode="normal" result="shape" />
              </filter>
              <linearGradient gradientUnits="userSpaceOnUse" id="paint0_linear_116_3" x1="683.671" x2="677.709" y1="-143.617" y2="576.329">
                <stop offset="0.25" stopColor="#FBFFF0" />
                <stop offset="0.384615" stopColor="#BBDC95" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
      <div className="topbar-logo-container">
        <Link to="/">
          <img src={CondoEatsLogo} alt="CondoEats Logo" className="topbar-logo" />
        </Link>
      </div>

      {/* Desktop Navigation */}
      <nav className="topbar-nav">
        <Link to="/about" className="topbar-nav-link">Home</Link>
        <Link to="/buy-food" className="topbar-nav-link">Buy Food</Link>
        {profile?.role === 'vendor' ? (
          <Link to="/sell-food" className="topbar-nav-link">Sell Food</Link>
        ) : (
          <Link to="/vendor" className="topbar-nav-link">Join As Vendor</Link>
        )}

        {/* Cart Dropdown */}
        <div className="cart-dropdown-container" ref={cartDropdownRef}>
          <button
            className="topbar-cart-button"
            onClick={toggleCartDropdown}
            title="Cart"
          >
            Cart
            {cartItems.length > 0 && (
              <span className="cart-badge">{cartItems.length}</span>
            )}
          </button>

          {cartDropdownOpen && (
            <div className="cart-dropdown">
              {/* Header */}
              <div className="cart-dropdown-header">
                <h3 className="cart-title">Cart</h3>
                <button
                  className="cart-close-btn"
                  onClick={closeCartDropdown}
                  aria-label="Close cart"
                >
                  ×
                </button>
              </div>

              {/* Cart Items */}
              <div className="cart-items-container">
                {loadingCart ? (
                  <div className="cart-loading">
                    <div className="cart-spinner"></div>
                    <p>Loading cart...</p>
                  </div>
                ) : cartError ? (
                  <div className="cart-error">
                    <p>{cartError}</p>
                  </div>
                ) : cartItems.length === 0 ? (
                  <div className="cart-empty">Your cart is empty</div>
                ) : (
                  <>
                    {cartItems.map((item) => (
                      <div key={item.id} className="cart-item">
                        <div className="cart-item-image-container">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="cart-item-image"
                            />
                          ) : (
                            <div className="cart-item-image-placeholder">
                              🍽️
                            </div>
                          )}
                        </div>

                        {/* Remove button */}
                        <button
                          className="cart-item-remove-btn"
                          onClick={() => handleDeleteClick(item.id)}
                          aria-label={`Delete ${item.name} from cart`}
                          title="Delete"
                        >
                          delete
                        </button>

                        <div className="cart-item-details">
                          <h4 className="cart-item-name">{item.name}</h4>

                          <div className="cart-item-info">
                            <p className="cart-item-vendor">Prepared by {item.vendor}</p>
                            <p className="cart-item-time">Order by: {item.orderBy}</p>
                            <p className="cart-item-time">Pickup by: {item.pickupBy}</p>
                            <p className="cart-item-pickup"><strong>Pickup Location:</strong> {item.pickupLocation}</p>
                          </div>
                        </div>

                        <div className="cart-item-quantity">
                          <div className="cart-item-price">RM {item.price} <span className="cart-item-price-unit">Each</span></div>
                          <div className="quantity-controls">
                            <button
                              className="quantity-btn"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              −
                            </button>
                            <span className="quantity-value">{item.quantity}</span>
                            <button
                              className="quantity-btn"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Subtotal and Checkout */}
                    <div className="cart-footer">
                      <div className="cart-subtotal">
                        <span className="subtotal-label">Subtotal</span>
                        <span className="subtotal-amount">RM {calculateSubtotal().toFixed(2)}</span>
                      </div>
                      <button
                        className="cart-checkout-btn"
                        onClick={handleReviewOrder}
                      >
                        Review Order
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {isAuthenticated && (
          <Link to="/orders" className="topbar-nav-link">Orders</Link>
        )}

        {isAuthenticated ? (
          <div className="profile-dropdown-container" ref={dropdownRef}>
            <button
              className="topbar-user-avatar"
              onClick={toggleProfileDropdown}
              title={user?.name || 'User'}
            >
              {getInitials(user?.name)}
            </button>

            {/* Profile Dropdown Menu */}
            {profileDropdownOpen && (
              <div className="profile-dropdown">
                {/* User Info Section */}
                <div className="profile-dropdown-header">
                  <div className="dropdown-avatar">
                    {getInitials(user?.name)}
                  </div>
                  <div className="dropdown-user-name">{profile?.full_name || user?.name || 'User'}</div>
                  <div className="dropdown-user-email">{user?.email || ''}</div>
                </div>

                {/* Wallet Balance and Points Combined Section */}
                <div className="dropdown-balance-points-wrapper">
                  {/* Wallet Balance Section - Connected to database */}
                  <div className="dropdown-balance-section">
                    <img
                      src={WalletIcon}
                      alt="Wallet"
                      className="balance-wallet-icon"
                    />
                    <div className="balance-content">
                      <div className="balance-info">
                        <div className="balance-label">Balance</div>
                        <div className="balance-amount">
                          RM {profile?.wallet_balance?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                      <div className="balance-buttons">
                        <button
                          className="balance-topup-btn"
                          onClick={() => navigate('/topup')}
                        >
                          + Top up
                        </button>
                        {profile?.role === 'vendor' && (
                          <button
                            className="balance-cashout-btn"
                            onClick={() => navigate('/cashout')}
                          >
                            Cash Out
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Points Section */}
                  <div className="dropdown-points-section">
                    <div className="points-header">
                      Points: {profile?.loyalty_points || 0}
                    </div>
                    <div className="points-description">
                      Earn points from every purchase and redeem them for discounts.
                    </div>
                  </div>
                </div>

                {/* Menu Options - Below Balance */}
                <div className="dropdown-menu-section">
                  {profile?.role === 'vendor' && (
                    <button
                      className="dropdown-menu-item"
                      onClick={() => {
                        closeProfileDropdown();
                        navigate(`/vendors/${user.$id}/reviews`);
                      }}
                    >
                      Profile
                    </button>
                  )}

                  <button
                    className="dropdown-menu-item dropdown-logout"
                    onClick={handleLogout}
                  >
                    Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <Link to="/login" className="topbar-nav-link">Login/Sign Up</Link>
        )}
      </nav>

      {/* Mobile Hamburger Button */}
      <button
        className={`topbar-hamburger ${mobileMenuOpen ? 'open' : ''}`}
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>
      )}

      {/* Mobile Navigation Menu */}
      <nav className={`topbar-mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
        {isAuthenticated ? (
          <>
            <div
              className={`topbar-mobile-user-info ${mobileUserMenuOpen ? 'expanded' : ''}`}
              onClick={toggleMobileUserMenu}
            >
              <span className="mobile-avatar-circle">{getInitials(user?.name)}</span>
              <div className="mobile-user-details">
                <span className="mobile-user-name">{user?.name || 'User'}</span>
                <span className="mobile-user-email">{user?.email || ''}</span>
              </div>
              <span className={`mobile-dropdown-arrow ${mobileUserMenuOpen ? 'open' : ''}`}>▼</span>
            </div>
            {mobileUserMenuOpen && (
              <div className="mobile-user-submenu">
                <div className="mobile-balance-section">
                  <div className="mobile-balance-info">
                    <div className="mobile-balance-label">Balance</div>
                    <div className="mobile-balance-amount">RM {profile?.wallet_balance?.toFixed(2) || '0.00'}</div>
                  </div>
                  <div className="mobile-balance-buttons">
                    <button className="mobile-topup-btn" onClick={() => navigate('/topup')}>
                      + Top up
                    </button>
                    {profile?.role === 'vendor' && (
                      <button className="mobile-cashout-btn" onClick={() => navigate('/cashout')}>
                        Cash Out
                      </button>
                    )}
                  </div>
                </div>
                {profile?.role === 'vendor' && (
                  <button className="topbar-mobile-link mobile-profile-btn mobile-submenu-item" onClick={() => { closeMobileMenu(); navigate(`/vendors/${user.$id}/reviews`); }}>
                    Profile
                  </button>
                )}

                <button className="topbar-mobile-link mobile-logout-btn mobile-submenu-item" onClick={handleLogout}>
                  Log out
                </button>
              </div>
            )}
          </>
        ) : (
          <Link to="/login" className="topbar-mobile-link" onClick={closeMobileMenu}>Login/Sign Up</Link>
        )}
        {!mobileUserMenuOpen && (
          <>
            <Link to="/about" className="topbar-mobile-link" onClick={closeMobileMenu}>Home</Link>
            <Link to="/buy-food" className="topbar-mobile-link" onClick={closeMobileMenu}>Buy Food</Link>
            {profile?.role === 'vendor' ? (
              <Link to="/sell-food" className="topbar-mobile-link" onClick={closeMobileMenu}>Sell Food</Link>
            ) : (
              <Link to="/vendor" className="topbar-mobile-link" onClick={closeMobileMenu}>Join As Vendor</Link>
            )}
            <div className="mobile-cart-link-container">
              <Link
                to="/cart"
                className="topbar-mobile-link"
                onClick={closeMobileMenu}
              >
                Cart
                {cartItems.length > 0 && (
                  <span className="mobile-cart-badge">{cartItems.length}</span>
                )}
              </Link>
            </div>
            {isAuthenticated && (
              <Link to="/orders" className="topbar-mobile-link" onClick={closeMobileMenu}>Orders</Link>
            )}
          </>
        )}
      </nav>

      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={confirmRemove}
        onCancel={cancelRemove}
        title="Remove Item"
        message="Remove this item from cart?"
      />
    </div>
  );
}